/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea, Stack, Text, Alert, Group } from '@/shared/ui';
import { DataRightType, DATA_RIGHTS_OPTIONS } from '../types/rights-types';

interface RightsRequestFormProps {
  selectedType: DataRightType;
  onSubmit: (description: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * RightsRequestForm component
 * Form for submitting data subject rights requests
 */
export const RightsRequestForm: React.FC<RightsRequestFormProps> = ({
  selectedType,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation('@pages/data-rights');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOption = DATA_RIGHTS_OPTIONS.find((opt) => opt.type === selectedType);
  const requiresAdditionalInfo = selectedOption?.requiresAdditionalInfo || false;

  const handleSubmit = async () => {
    if (requiresAdditionalInfo && !description.trim()) {
      setError(t('dataRights.form.descriptionRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(description);
    } catch (err) {
      setError(t('dataRights.form.submitError'));
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      <div>
        <Text size="lg" fw={600} mb="xs">
          {t(selectedOption?.titleKey || '')}
        </Text>
        <Text size="sm" className="text-(--text-secondary)">
          {t(selectedOption?.descriptionKey || '')}
        </Text>
      </div>

      <Alert color="blue" title={t('dataRights.form.legalNoticeTitle')}>
        <Text size="sm">{t('dataRights.form.legalNotice')}</Text>
        <Text size="sm" mt="xs">
          {t('dataRights.form.responseTime')}
        </Text>
      </Alert>

      <Stack gap="xs">
        <Text size="sm" fw={500} className="text-(--text-primary)">
          {t('dataRights.form.descriptionLabel')}
          {requiresAdditionalInfo && (
            <Text component="span" className="text-(--status-error-text)"> *</Text>
          )}
        </Text>
        <Text size="xs" className="text-(--text-secondary)">
          {requiresAdditionalInfo
            ? t('dataRights.form.descriptionRequired')
            : t('dataRights.form.descriptionOptional')}
        </Text>
        <Textarea
          placeholder={t('dataRights.form.descriptionPlaceholder')}
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
          rows={4}
          required={requiresAdditionalInfo}
          aria-invalid={!!error}
          aria-label={t('dataRights.form.descriptionLabel')}
        />
        {error && (
          <Text size="xs" className="text-(--status-error-text)">
            {error}
          </Text>
        )}
      </Stack>

      <Text size="xs" className="text-(--text-secondary)">
        {t('dataRights.form.privacyNote')}
      </Text>

      <Group justify="flex-end" className="mt-(--space-4)">
        <Button variant="subtle" onClick={onCancel} disabled={isSubmitting}>
          {t('dataRights.form.cancel')}
        </Button>
        <Button onClick={handleSubmit} loading={isSubmitting}>
          {t('dataRights.form.submit')}
        </Button>
      </Group>
    </Stack>
  );
};
